/**
 * Build an image URL constrained to `width` for the three hosts we serve:
 *   • Pexels / Unsplash — use their built-in query params.
 *   • Our own /api/files/… — use the ?w=<width> variant endpoint that returns
 *     a WebP resized on the fly (with in-process cache + Cloudflare cache).
 *   • Anything else — return the raw URL unchanged.
 */
const OWN_FILES_MARK = "/api/files/";
const OWN_VARIANT_WIDTHS = [200, 400, 800, 1600];

function nearestOwnWidth(width) {
  // Snap to the nearest whitelisted variant so we hit the cache instead of
  // asking the server to render arbitrary widths.
  return OWN_VARIANT_WIDTHS.reduce((best, w) =>
    Math.abs(w - width) < Math.abs(best - width) ? w : best,
  OWN_VARIANT_WIDTHS[0]);
}

export function optimizedImg(url, width = 400) {
  if (!url || typeof url !== "string") return url;
  if (url.includes("images.pexels.com")) {
    const [base] = url.split("?");
    return `${base}?auto=compress&cs=tinysrgb&w=${width}&dpr=1`;
  }
  if (url.includes("images.unsplash.com")) {
    const [base] = url.split("?");
    return `${base}?auto=format&fit=crop&w=${width}&q=75`;
  }
  if (url.includes(OWN_FILES_MARK)) {
    const [base] = url.split("?");
    return `${base}?w=${nearestOwnWidth(width)}`;
  }
  return url;
}

/** Build a responsive srcset string for supported hosts (returns undefined
 * for unknown hosts so <img> falls back to the plain src). */
export function srcsetFor(url, widths = [200, 400, 800]) {
  if (!url) return undefined;
  const isPexels = url.includes("images.pexels.com");
  const isUnsplash = url.includes("images.unsplash.com");
  const isOwn = url.includes(OWN_FILES_MARK);
  if (!isPexels && !isUnsplash && !isOwn) return undefined;
  // Our own storage caps at the whitelisted widths; snap each requested width
  // to its nearest allowed variant to guarantee cache hits.
  const effective = isOwn ? widths.map(nearestOwnWidth) : widths;
  // De-duplicate while preserving order (nearestOwnWidth can collapse).
  const seen = new Set();
  const uniq = effective.filter((w) => (seen.has(w) ? false : (seen.add(w), true)));
  return uniq.map((w) => `${optimizedImg(url, w)} ${w}w`).join(", ");
}
